import {PartOfSpeech, WordWithId} from "../../../../common/types/words";
import {Box, Button, FormControl, Typography} from "@mui/material";
import React, {useState} from "react";
import {UpdateWord} from "../../types/word_operations";
import {useApiRequest} from "../../../../common/hooks/useApiRequest";
import {FocusFormHandle, FormValidationHandle, WordInfoEditView} from "./WordInfoEditView";
import {useSnackbar} from "../../../../common/context/SnackbarContext";
import withDrawer, {SideDrawerProps} from "../../../../common/components/SideDrawerView";
import {RequestStatusSnackbarConfig} from "../../../../common/types/requests";
import {VocabularyActionDispatch} from "../../pages/Vocabulary";
import {useMutation} from "@tanstack/react-query";

export interface WordUpdateViewProps extends SideDrawerProps {
    word: WordWithId
    actionDispatch?: VocabularyActionDispatch
    snackbar?: RequestStatusSnackbarConfig
}

function WordUpdateView({word, actionDispatch, snackbar} : WordUpdateViewProps) {

    const [updateData, setUpdateData] = useState<UpdateWord>({
        lemma: word.lemma,
        translations: word.translations,
        exampleSentences: word.exampleSentences.map(({sentence, translation}) => ({sentence, translation, audio: null})),
        partOfSpeech: word.partOfSpeech || PartOfSpeech.Unknown,
        audio: null,
    });

    const {patchRequest} = useApiRequest();
    const {showSnackbar} = useSnackbar();

    const wordEditFormRef = React.useRef<FormValidationHandle & FocusFormHandle>(null);

    const {mutateAsync: executeUpdateWord} = useMutation({
        mutationFn: (data: UpdateWord) => patchRequest<WordWithId>(`words/${word.wordId}`, data),
        onSuccess: () => snackbar?.showRequestSuccessSnackbar && showSnackbar("Word saved successfully!", "success"),
        onError: () => snackbar?.showRequestErrorSnackbar && showSnackbar("Failed to save word.", "error")
    });

    const saveButtonClicked = () => {
        if (!wordEditFormRef.current!.validateForm()) {
            return;
        }
        snackbar?.showRequestSentSnackbar && showSnackbar("Saving word...", "info");

        const onUpdatePromise: Promise<WordWithId> = executeUpdateWord(updateData);
        !!actionDispatch?.update && actionDispatch.update({...updateData, wordId: word.wordId, morphology: word.morphology, resources: word.resources}, onUpdatePromise);
    }


    return (
        <FormControl sx={{width: '100%'}}>
            <Typography variant="h5" align="center" fontWeight="bold">Edit word</Typography>
            <Box sx={{mt: 2}}/>
            <WordInfoEditView existingWord={word}
                              wordConfig={updateData}
                              onChange={(partialUpdate) => setUpdateData(prev => ({...prev, ...partialUpdate}))}
                              includeFullGenerationButton={false}
                              ref={wordEditFormRef}
            />
            <Button variant={"contained"}
                    color={"secondary"}
                    fullWidth={true}
                    onClick={saveButtonClicked}
                    sx={{borderRadius: 3}}
            >
                Save
            </Button>
            <Box sx={{mt: 1}}/>
        </FormControl>
    )

}

export default withDrawer<WordUpdateViewProps>(WordUpdateView);